import { useState } from "react";
import { Button } from "../ui/button";
import {
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Copy,
  FileText,
  GripVertical,
} from "lucide-react";

/**
 * MultiPageManager - Manages multiple pages in a document
 */
export function MultiPageManager({
  pages,
  currentPageIndex,
  onPageChange,
  onAddPage,
  onDeletePage,
  onDuplicatePage,
  onReorderPages,
}) {
  const [draggingIndex, setDraggingIndex] = useState(null);

  const handlePrevPage = () => {
    if (currentPageIndex > 0) {
      onPageChange(currentPageIndex - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPageIndex < pages.length - 1) {
      onPageChange(currentPageIndex + 1);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === index) return;
    
    // Reorder pages
    const newPages = [...pages];
    const draggedPage = newPages.splice(draggingIndex, 1)[0];
    newPages.splice(index, 0, draggedPage);
    
    setDraggingIndex(index);
    onReorderPages(newPages, index);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
  };

  return (
    <div className="flex flex-col">
      {/* Page Navigation Bar */}
      <div className="flex items-center justify-between bg-white border-b border-slate-200 px-3 py-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrevPage}
            disabled={currentPageIndex === 0}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <span className="text-sm font-medium text-slate-600 min-w-[80px] text-center">
            Page {currentPageIndex + 1} / {pages.length}
          </span>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPageIndex === pages.length - 1}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onAddPage}
            title="Ajouter une page"
          >
            <Plus className="w-4 h-4 mr-1" />
            Page
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDuplicatePage(currentPageIndex)}
            title="Dupliquer cette page"
          >
            <Copy className="w-4 h-4" />
          </Button>
          
          {pages.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDeletePage(currentPageIndex)}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              title="Supprimer cette page"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Page Thumbnails (horizontal scrollable) */}
      <div className="flex gap-2 p-2 bg-slate-50 overflow-x-auto">
        {pages.map((page, index) => (
          <div
            key={page.id || index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            onClick={() => onPageChange(index)}
            className={`relative flex-shrink-0 cursor-pointer transition-all ${
              index === currentPageIndex
                ? "ring-2 ring-indigo-500 shadow-md"
                : "hover:ring-2 hover:ring-slate-300"
            } ${draggingIndex === index ? "opacity-50" : ""}`}
          >
            {/* Thumbnail */}
            <div
              className="w-16 h-20 bg-white border border-slate-200 rounded flex items-center justify-center overflow-hidden"
            >
              {page.thumbnail ? (
                <img
                  src={page.thumbnail}
                  alt={`Page ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <FileText className="w-6 h-6 text-slate-300" />
              )}
            </div>
            
            {/* Page number */}
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-slate-700 text-white text-xs px-1.5 py-0.5 rounded">
              {index + 1}
            </div>
            
            {/* Drag handle */}
            <div className="absolute top-0 left-0 p-0.5 text-slate-400 hover:text-slate-600">
              <GripVertical className="w-3 h-3" />
            </div>
          </div>
        ))}
        
        {/* Add page button */}
        <button
          onClick={onAddPage}
          className="flex-shrink-0 w-16 h-20 border-2 border-dashed border-slate-300 rounded flex items-center justify-center hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
        >
          <Plus className="w-6 h-6 text-slate-400" />
        </button>
      </div>
    </div>
  );
}

/**
 * PageThumbnail - Single page thumbnail component
 */
export function PageThumbnail({ page, index, isActive, onClick, onDelete }) {
  return (
    <div
      onClick={onClick}
      className={`relative cursor-pointer transition-all ${
        isActive
          ? "ring-2 ring-indigo-500 shadow-md scale-105"
          : "hover:ring-2 hover:ring-slate-300"
      }`}
    >
      <div className="w-12 h-16 bg-white border border-slate-200 rounded flex items-center justify-center">
        {page.thumbnail ? (
          <img
            src={page.thumbnail}
            alt={`Page ${index + 1}`}
            className="w-full h-full object-cover rounded"
          />
        ) : (
          <span className="text-xs text-slate-400">{index + 1}</span>
        )}
      </div>
      
      {isActive && onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
        >
          ×
        </button>
      )}
    </div>
  );
}

export default MultiPageManager;
